/**
 * Goal Verification Page
 * 
 * Tests and displays the status of both backplan goals:
 * - Product Backplan: Production-quality enterprise SaaS
 * - Adoption Backplan: First customer successfully using platform
 */

import React, { useState, useEffect } from 'react';
import { 
  Target, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Loader2, 
  RefreshCw,
  Building2,
  Users,
  Shield,
  Gauge,
  FileCheck
} from 'lucide-react';
import { verifyProductionGoal } from '../lib/productionReadiness';
import { verifyCustomerSuccessGoal, calculateSuccessScore } from '../lib/customerSuccess';
import { evaluateProductionReadiness } from '../lib/productionReadiness';

interface GoalResult {
  achieved: boolean;
  evidence: string[];
  gaps: string[];
}

export const GoalVerification: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [productGoal, setProductGoal] = useState<GoalResult | null>(null);
  const [adoptionGoal, setAdoptionGoal] = useState<GoalResult | null>(null);
  const [productReadiness, setProductReadiness] = useState<Awaited<ReturnType<typeof evaluateProductionReadiness>> | null>(null);
  const [customerSuccess, setCustomerSuccess] = useState<Awaited<ReturnType<typeof calculateSuccessScore>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runVerification = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Run both goal verifications in parallel
      const [prodResult, adoptResult, readiness] = await Promise.all([
        verifyProductionGoal(),
        verifyCustomerSuccessGoal('demo-customer-001'),
        evaluateProductionReadiness('1.0.0'),
      ]);
      
      setProductGoal(prodResult);
      setAdoptionGoal(adoptResult);
      setProductReadiness(readiness);
      
      // Also calculate customer success score for display
      const successScore = await calculateSuccessScore('demo-customer-001', 'Demo Customer');
      setCustomerSuccess(successScore);
    } catch (err: any) {
      setError(err.message || 'Failed to run goal verification');
      console.error('Goal verification error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runVerification();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-12 w-12 animate-spin text-[var(--text-ter)]" />
            <span className="ml-4 text-xl text-[var(--text-sec)]">Running Goal Verification...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-8 text-center">
            <XCircle className="h-12 w-12 text-[var(--coral)] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[var(--coral)] mb-2">Verification Failed</h2>
            <p className="text-[var(--coral)]">{error}</p>
            <button
              onClick={runVerification}
              className="mt-4 px-4 py-2 bg-[var(--coral)] text-white hover:bg-[var(--coral)] flex items-center gap-2 mx-auto"
            >
              <RefreshCw className="h-4 w-4" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const bothAchieved = productGoal?.achieved && adoptionGoal?.achieved;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-3  ${bothAchieved ? 'bg-[var(--green-dim)]' : 'bg-[var(--mustard-dim)]'}`}>
              <Target className={`h-8 w-8 ${bothAchieved ? 'text-[var(--green)]' : 'text-[var(--mustard)]'}`} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[var(--text-pri)]">Backplan Goal Verification</h1>
              <p className="text-[var(--text-sec)] mt-1">Testing achievement of both backplan goals</p>
            </div>
          </div>
          <button
            onClick={runVerification}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Re-run Verification
          </button>
        </div>

        {/* Overall Status */}
        <div className={` p-8 ${bothAchieved ? 'bg-[var(--green-dim)] border-2 border-[var(--green)]' : 'bg-[var(--mustard-dim)] border-2 border-[var(--mustard)]'}`}>
          <div className="flex items-center gap-4">
            {bothAchieved ? (
              <CheckCircle className="h-16 w-16 text-[var(--green)]" />
            ) : (
              <AlertTriangle className="h-16 w-16 text-[var(--mustard)]" />
            )}
            <div>
              <h2 className={`text-2xl font-bold ${bothAchieved ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--mustard)]'}`}>
                {bothAchieved ? '🎉 BOTH GOALS ACHIEVED!' : 'Goals In Progress'}
              </h2>
              <p className={`text-lg ${bothAchieved ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--mustard)]'}`}>
                {bothAchieved 
                  ? 'Production-quality enterprise SaaS with successful first customer'
                  : `Product Goal: ${productGoal?.achieved ? '✓' : '○'} | Adoption Goal: ${adoptionGoal?.achieved ? '✓' : '○'}`}
              </p>
            </div>
          </div>
        </div>

        {/* Goal Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Product Goal */}
          <GoalCard
            title="Product Goal"
            subtitle="Production-quality enterprise SaaS"
            icon={Building2}
            result={productGoal}
            metrics={productReadiness ? [
              { label: 'Readiness Score', value: `${productReadiness.readinessScore}%`, threshold: 90 },
              { label: 'Gates Passed', value: `${productReadiness.gates.filter(g => g.status === 'passed').length}/${productReadiness.gates.length}` },
              { label: 'Blockers', value: productReadiness.blockers.length.toString(), isGood: productReadiness.blockers.length === 0 },
              { label: 'Can Release', value: productReadiness.ready ? 'Yes' : 'No', isGood: productReadiness.ready },
            ] : []}
          />

          {/* Adoption Goal */}
          <GoalCard
            title="Adoption Goal"
            subtitle="First customer successfully using platform"
            icon={Users}
            result={adoptionGoal}
            metrics={customerSuccess ? [
              { label: 'Success Score', value: `${customerSuccess.successScore}%`, threshold: 70 },
              { label: 'Status', value: customerSuccess.successStatus.replace('_', ' '), isGood: customerSuccess.successStatus === 'thriving' || customerSuccess.successStatus === 'healthy' },
              { label: 'Milestones', value: `${customerSuccess.milestones.filter(m => m.achieved).length}/${customerSuccess.milestones.length}` },
              { label: 'Value Score', value: `${customerSuccess.valueMetrics.valueScore}%`, threshold: 60 },
            ] : []}
          />
        </div>

        {/* Production Readiness Details */}
        {productReadiness && (
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
            <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-[var(--text-ter)]" />
              Production Readiness Categories
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {Object.entries(productReadiness.categories).map(([category, stats]) => (
                <div 
                  key={category} 
                  className={`p-4  ${stats.score >= 80 ? 'bg-[var(--green-dim)]' : stats.score >= 50 ? 'bg-[var(--mustard-dim)]' : 'bg-[var(--coral-dim)]'}`}
                >
                  <div className="text-sm font-medium text-[var(--text-sec)] capitalize">{category}</div>
                  <div className={`text-2xl font-bold ${stats.score >= 80 ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : stats.score >= 50 ? 'text-[var(--mustard)]' : 'text-[var(--coral)]'}`}>
                    {stats.score}%
                  </div>
                  <div className="text-xs text-[var(--text-ter)]">
                    {stats.passed} passed / {stats.failed} failed
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Customer Success Milestones */}
        {customerSuccess && (
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
            <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-[var(--text-ter)]" />
              Customer Success Milestones
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {customerSuccess.milestones.map((milestone) => (
                <div 
                  key={milestone.milestone} 
                  className={`p-4  ${milestone.achieved ? 'bg-[var(--green-dim)]' : 'bg-[var(--surface)]'}`}
                >
                  <div className="flex items-center gap-2">
                    {milestone.achieved ? (
                      <CheckCircle className="h-4 w-4 text-[var(--green)]" />
                    ) : (
                      <div className="h-4 w-4 rounded-full border-2 border-[var(--rule)]" />
                    )}
                    <span className={`text-sm font-medium ${milestone.achieved ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-sec)]'}`}>
                      {milestone.milestone.replace(/_/g, ' ')}
                    </span>
                  </div>
                  {milestone.evidence && (
                    <p className="text-xs text-[var(--text-ter)] mt-1 ml-6">{milestone.evidence}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quality Gates */}
        {productReadiness && (
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
            <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
              <Gauge className="h-5 w-5 text-[var(--text-ter)]" />
              Quality Gates ({productReadiness.gates.filter(g => g.status === 'passed').length}/{productReadiness.gates.length} passing)
            </h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {productReadiness.gates.map((gate) => (
                <div 
                  key={gate.id} 
                  className={`flex items-center justify-between p-3  ${
                    gate.status === 'passed' ? 'bg-[var(--green-dim)]' : 
                    gate.status === 'warning' ? 'bg-[var(--mustard-dim)]' : 'bg-[var(--coral-dim)]'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {gate.status === 'passed' ? (
                      <CheckCircle className="h-5 w-5 text-[var(--green)]" />
                    ) : gate.status === 'warning' ? (
                      <AlertTriangle className="h-5 w-5 text-[var(--mustard)]" />
                    ) : (
                      <XCircle className="h-5 w-5 text-[var(--coral)]" />
                    )}
                    <div>
                      <div className="font-medium text-[var(--text-pri)]">{gate.name}</div>
                      <div className="text-xs text-[var(--text-ter)]">{gate.description}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-1 rounded ${
                      gate.status === 'passed' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' :
                      gate.status === 'warning' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' : 'bg-[var(--coral-dim)] text-[var(--coral)]'
                    }`}>
                      {gate.category}
                    </span>
                    {gate.required && (
                      <span className="ml-2 text-xs px-2 py-1 rounded bg-[var(--slate-dim)] text-[var(--text-sec)]">
                        Required
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Goal Card Component
interface GoalCardProps {
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  result: GoalResult | null;
  metrics: { label: string; value: string; threshold?: number; isGood?: boolean }[];
}

const GoalCard: React.FC<GoalCardProps> = ({ title, subtitle, icon: Icon, result, metrics }) => {
  if (!result) return null;

  return (
    <div className={` shadow-sm border-2 p-6 ${
      result.achieved ? 'bg-[var(--green-dim)] border-[var(--green)]' : 'bg-[var(--bg-raised)] border-[var(--mustard)]'
    }`}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2  ${result.achieved ? 'bg-[var(--green-dim)]' : 'bg-[var(--mustard-dim)]'}`}>
          <Icon className={`h-6 w-6 ${result.achieved ? 'text-[var(--green)]' : 'text-[var(--mustard)]'}`} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-[var(--text-pri)]">{title}</h3>
          <p className="text-sm text-[var(--text-ter)]">{subtitle}</p>
        </div>
        {result.achieved ? (
          <CheckCircle className="h-8 w-8 text-[var(--green)] ml-auto" />
        ) : (
          <AlertTriangle className="h-8 w-8 text-[var(--mustard)] ml-auto" />
        )}
      </div>

      {/* Metrics */}
      {metrics.length > 0 && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          {metrics.map((metric, i) => (
            <div key={i} className="bg-[var(--bg-raised)]/50 p-3">
              <div className="text-xs text-[var(--text-ter)]">{metric.label}</div>
              <div className={`text-lg font-semibold ${
                metric.isGood !== undefined 
                  ? (metric.isGood ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--coral)]')
                  : metric.threshold !== undefined
                    ? (parseInt(metric.value) >= metric.threshold ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--mustard)]')
                    : 'text-[var(--text-pri)]'
              }`}>
                {metric.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Evidence */}
      {result.evidence.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)] mb-2">✓ Evidence</h4>
          <ul className="space-y-1">
            {result.evidence.slice(0, 5).map((item, i) => (
              <li key={i} className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] flex items-start gap-2">
                <CheckCircle className="h-3 w-3 mt-1 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
            {result.evidence.length > 5 && (
              <li className="text-xs text-[var(--green)] dark:text-[var(--text-ter)]">...and {result.evidence.length - 5} more</li>
            )}
          </ul>
        </div>
      )}

      {/* Gaps */}
      {result.gaps.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-[var(--coral)] mb-2">✗ Gaps</h4>
          <ul className="space-y-1">
            {result.gaps.slice(0, 5).map((item, i) => (
              <li key={i} className="text-sm text-[var(--coral)] flex items-start gap-2">
                <XCircle className="h-3 w-3 mt-1 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
            {result.gaps.length > 5 && (
              <li className="text-xs text-[var(--coral)]">...and {result.gaps.length - 5} more</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default GoalVerification;


